import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Team = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await axios.get(`${API}/team`);
        setTeamMembers(response.data);
      } catch (error) {
        console.error("Error fetching team:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, []);

  return (
    <div data-testid="team-page" className="pt-24">
      {/* Hero Section */}
      <section className="py-16 relative">
        <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/20 rounded-full blur-3xl -z-10" />
        
        <div className="section-container">
          <div className="text-center max-w-3xl mx-auto animate-fade-in">
            <span className="text-xs tracking-widest uppercase font-bold text-primary/60 mb-4 block">
              Our Team
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-primary leading-tight mb-6">
              Meet the Caring Professionals Behind Konastone
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Our dedicated team combines expertise with genuine compassion 
              to deliver exceptional care to every client we serve.
            </p>
          </div>
        </div>
      </section>

      {/* Team Grid */}
      <section className="py-16">
        <div className="section-container">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-xl overflow-hidden animate-pulse">
                  <div className="h-64 bg-gray-200" />
                  <div className="p-6">
                    <div className="h-6 bg-gray-200 rounded mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {teamMembers.map((member, index) => (
                <div
                  key={member.id}
                  data-testid={`team-member-${index}`}
                  className="bg-white border border-primary/10 rounded-xl p-6 animate-fade-in hover:shadow-lg transition-all duration-300"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <span className="font-serif font-bold text-2xl text-primary">
                      {member.name.charAt(0)}
                    </span>
                  </div>
                  <h3 className="font-serif font-semibold text-primary text-xl mb-1">
                    {member.name}
                  </h3>
                  <p className="text-accent font-medium mb-3">{member.role}</p>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                    {member.bio}
                  </p>
                  {member.qualifications && member.qualifications.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {member.qualifications.map((qual, qIndex) => (
                        <span
                          key={qIndex}
                          className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full"
                        >
                          {qual}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Join Team Section */}
      <section className="py-20 bg-white">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-in">
              <img
                src="https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?w=600"
                alt="Join our team"
                className="w-full h-[400px] object-cover rounded-2xl shadow-lg"
              />
            </div>

            <div className="animate-fade-in stagger-2">
              <span className="text-xs tracking-widest uppercase font-bold text-primary/60 mb-4 block">
                Careers
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-primary mb-6">
                Join Our Caring Team
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Are you passionate about making a difference in people&apos;s lives? 
                We&apos;re always looking for dedicated individuals who share our 
                commitment to exceptional care.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-8">
                We offer competitive pay, comprehensive training, flexible hours, 
                and the opportunity to build meaningful relationships with clients 
                and their families.
              </p>
              <Link to="/contact">
                <Button
                  data-testid="join-team-btn"
                  className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-6 text-lg font-medium transition-all hover:scale-105"
                >
                  Get in Touch
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="section-container">
          <div className="text-center animate-fade-in">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white mb-4">
              Want to Learn More About Our Care?
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Contact us today to discuss how our team can support you or your loved one.
            </p>
            <Link to="/contact">
              <Button
                data-testid="team-cta-btn"
                className="bg-accent hover:bg-accent/90 text-white rounded-full px-8 py-6 text-lg font-medium transition-all hover:scale-105"
              >
                Book Free Assessment
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
